import { useGetAppConfigurations } from '../../../features/user/queries';
import { ReactNode, useEffect, useMemo, useRef } from 'react';
import { safeParse } from 'secure-json-parse';
import {
  AppConfigurationsContext,
  AppConfigurationsData,
} from './AppConfigurationsContext';

interface AppConfigurationsProviderProps {
  children: ReactNode;
}

// Configuration
const MAX_RETRIES = 5; // cap real (error) retries
const BASE_DELAY_MS = 250; // backoff start
const MAX_DELAY_MS = 4000; // backoff ceiling

/**
 * AppConfigurationsProvider
 *
 * - Lazily fetches app configurations once the component is mounted.
 * - Handles two retry channels:
 *    1) "Skipped" attempts (auth gate short-circuited the request): separate backoff, does NOT count toward MAX_RETRIES.
 *    2) Real errors (network/GQL): exponential backoff, DOES count toward MAX_RETRIES.
 * - Assumes the auth gate (in Root.tsx) returns a shape-correct skipped payload:
 *     { data: { getAppConfigurations: null }, extensions: { skippedByAuthGate: true } }
 * - Safe parsing of JSON payload via `safeParse`.
 */
export const AppConfigurationsProvider = ({
  children,
}: AppConfigurationsProviderProps) => {
  const [
    getAppConfigurations,
    { loading: appConfigurationsLoading, data: appConfigurationsData },
  ] = useGetAppConfigurations({
    // First load should hit the network; afterward the cache will be used unless options override.
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  // Timer / retry state (no NodeJS.Timeout typing to keep DOM compatibility)
  const scheduledRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const errorRetriesRef = useRef<number>(0); // counts only real errors (from onError)
  const skipDelayRef = useRef<number>(BASE_DELAY_MS); // backoff for skipped attempts (auth not ready)

  // Parse the configuration JSON (if present)
  const appConfigurations = useMemo<AppConfigurationsData | null>(() => {
    const rawData = appConfigurationsData?.getAppConfigurations?.body?.data;
    if (!rawData) return null;
    try {
      return safeParse(rawData);
    } catch {
      return null;
    }
  }, [appConfigurationsData]);

  useEffect(() => {
    // If we already have configurations, reset counters and exit.
    if (appConfigurations) {
      errorRetriesRef.current = 0;
      skipDelayRef.current = BASE_DELAY_MS;
      return;
    }

    // If a fetch is in-flight or already scheduled, do nothing.
    if (appConfigurationsLoading || scheduledRef.current) return;

    // Detect a "skipped" result from the auth gate:
    // Root.tsx emits: { data: { getAppConfigurations: null }, extensions: { skippedByAuthGate: true } }
    const wasSkipped =
      appConfigurationsData !== undefined &&
      appConfigurationsData?.getAppConfigurations === null;

    // If skipped, widen the skip backoff (but do NOT consume error retries)
    if (wasSkipped) {
      skipDelayRef.current = Math.min(
        (skipDelayRef.current || BASE_DELAY_MS) * 2,
        MAX_DELAY_MS,
      );
    }

    // Compute delay using the larger of the two backoffs
    const errorBackoff = Math.min(
      BASE_DELAY_MS * Math.pow(2, errorRetriesRef.current || 0),
      MAX_DELAY_MS,
    );
    const delay = Math.max(skipDelayRef.current || BASE_DELAY_MS, errorBackoff);

    // If we’ve exhausted error retries and this isn’t a "skipped" attempt, stop retrying.
    if (!wasSkipped && errorRetriesRef.current >= MAX_RETRIES) {
      return;
    }

    // Schedule a single attempt (don’t clear on each render; only on unmount or when fired)
    scheduledRef.current = setTimeout(() => {
      scheduledRef.current = null;

      getAppConfigurations({
        // If completed but still null (skipped/empty), the effect will reschedule with backoff.
        onCompleted: () => {
          // no-op
        },
        onError: () => {
          // Only real errors consume retry budget; also widen skip backoff a bit to prevent hammering.
          errorRetriesRef.current += 1;
          skipDelayRef.current = Math.min(
            (skipDelayRef.current || BASE_DELAY_MS) * 2,
            MAX_DELAY_MS,
          );
        },
      });
    }, delay);

    // Cleanup only on unmount
    return () => {
      if (scheduledRef.current) {
        clearTimeout(scheduledRef.current);
        scheduledRef.current = null;
      }
    };
  }, [
    appConfigurations,
    appConfigurationsLoading,
    appConfigurationsData,
    getAppConfigurations,
  ]);

  const contextValue = useMemo(
    () => ({
      appConfigurations,
      appConfigurationsLoading,
    }),
    [appConfigurations, appConfigurationsLoading],
  );

  return (
    <AppConfigurationsContext.Provider value={contextValue}>
      {children}
    </AppConfigurationsContext.Provider>
  );
};
